/* global google */
import React, { useCallback } from 'react'
import { Marker } from '@react-google-maps/api'
import { Clusterer } from '@react-google-maps/marker-clusterer'

import pins from './pins'
import type { PinSizing } from './pins'
import config from 'constants/config'
import useToggle from 'hooks/useToggle'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'

function scaleDownSizing(sizing: PinSizing): PinSizing {
  const SCALE = 5 / 6
  const { size, anchor } = sizing
  return {
    size: [size[0] * SCALE, size[1] * SCALE],
    anchor: [anchor[0] * SCALE, anchor[1] * SCALE],
  }
}

interface Props {
  active: boolean;
  visited: boolean;
  soldOut: boolean;
  marker: App.SearchMapMarker;
  onClick: (marker: App.SearchMapMarker, pinType: string) => void;
  clusterer?: Clusterer;
  scaleDown?: boolean;
  onLoad?: (markerInstance: google.maps.Marker, markerData: App.SearchMapMarker) => void;
  onUnmount?: (markerInstance: google.maps.Marker) => void;
}

function PropertyMarker(props: React.PropsWithChildren<Props>) {
  const {
    active,
    visited,
    soldOut,
    marker,
    clusterer,
    scaleDown,
    onClick,
    onLoad,
    onUnmount,
    children,
  } = props

  const { value: hovered, on: hoverOn, off: hoverOff } = useToggle()

  const isAllBedbank = marker.offers.every(offer => offer.type === OFFER_TYPE_BED_BANK)

  const isAllFlash = marker.offers.every(offer => offer.type === OFFER_TYPE_HOTEL)

  const brandPinConfig = pins[config.BRAND] ?? pins.default

  let pinIcons = brandPinConfig.pinIcons
  if (soldOut) {
    pinIcons = brandPinConfig.soldOutPinIcons
  } else if (isAllBedbank) {
    pinIcons = brandPinConfig.bedbankPinIcons
  } else if (isAllFlash) {
    pinIcons = brandPinConfig.flashPinIcons
  }

  let pinVersion = pinIcons.default
  if (active) {
    pinVersion = pinIcons.active
  } else if (visited) {
    pinVersion = pinIcons.previewed
  }

  const pin = hovered ? pinVersion.hover : pinVersion.default
  const { image, sizing: fullSizing } = pin

  const sizing = scaleDown ? scaleDownSizing(fullSizing) : fullSizing

  const iconSize = new google.maps.Size(...sizing.size)
  const anchor = new google.maps.Point(...sizing.anchor)
  const position = { lat: marker.latitude, lng: marker.longitude }
  const iconObject = { url: `${image}`, scaledSize: iconSize, anchor }

  const loadHandler = useCallback((markerInstance: google.maps.Marker) => {
    if (onLoad) {
      onLoad(markerInstance, marker)
    }
  }, [marker, onLoad])

  const clickHandler = useCallback(() => { onClick(marker, 'pin') }, [onClick, marker])

  let zIndex: number = 0

  if (active) {
    zIndex = 4
  } else if (isAllFlash) {
    zIndex = 3
  } else if (!isAllBedbank) {
    zIndex = 2
  } else if (isAllBedbank) {
    zIndex = 1
  }

  return (
    <Marker
      clusterer={clusterer}
      position={position}
      icon={iconObject}
      onLoad={loadHandler}
      onUnmount={onUnmount}
      onMouseOver={hoverOn}
      onMouseOut={hoverOff}
      onClick={clickHandler}
      zIndex={zIndex}
    >
      {children}
    </Marker>
  )
}

export default React.memo(PropertyMarker)
